import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    styled,
    Tooltip,
} from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import DraggableSelector from "../../../components/draggable-selector";
import MultiSelectList from "../../../components/multi-select-list/multi-select-list";
import { IListItem } from "../../../interface/list-item";
import { IRootState } from "../../../redux/root.reducer";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { flatOptions } from "../../../util/flat-list";
import { FormParams } from "../enums/form-params";
import { Steps } from "../enums/steps";
import { ICustomListsForm, IError } from "../interface/form";

const defaultOption = [{ code: "", name: "" }];

const selector = createSelector(
    (state: IRootState) => state.sicCodes,
    (state: IRootState) => state.naicsCodes,
    (state: IRootState) => state.categories,
    (state: IRootState) => state.listBuilder.listBuilderList,
    (state: IRootState) => state.listBuilder.listBuilder.data,
    (sicCodes, naicsCodes, categories, listBuilderList, listBuilder) => ({
        addTrackedLists: !!listBuilder?.addTrackedLists,
        primaryNaicsCodesOnly: !!listBuilder?.primaryNaicsCodesOnly,
        listBuilderList: {
            options: listBuilderList.lists.map(({ listId, name }) => ({
                id: listId.toString(),
                name,
            })),
            isLoading: listBuilderList.isLoading,
            selected: listBuilder
                ? listBuilderList.lists
                      .filter(({ listId }) =>
                          listBuilder[FormParams.LISTIDS].includes(listId),
                      )
                      .map(({ listId, name }) => ({
                          id: listId.toString(),
                          name,
                      }))
                : [],
        },
        trackedLists: {
            list: (listBuilderList.trackedLists ?? []).map(
                ({ listId, name }) => ({
                    id: listId.toString(),
                    name,
                }),
            ),
            isLoading: listBuilderList.isTrackedListsLoading,
        },
        sicCodes: {
            options: (sicCodes.data ?? defaultOption).map(({ code, name }) => ({
                id: code,
                name: `${code} - ${name}`,
            })),
            selected: listBuilder
                ? sicCodes.data
                      ?.filter(({ code }) =>
                          listBuilder[FormParams.SIC_CODES].includes(
                              Number(code),
                          ),
                      )
                      .map(({ code, name }) => ({
                          id: code,
                          name: `${code} - ${name}`,
                      }))
                : [],
            isLoading: sicCodes.isLoading,
        },
        naicsCodes: {
            options: (naicsCodes.data ?? defaultOption).map(
                ({ code, name }) => ({ id: code, name: `${code} - ${name}` }),
            ),
            selected: listBuilder
                ? naicsCodes.data
                      ?.filter(({ code }) =>
                          listBuilder[FormParams.NAICS_CODES].includes(
                              Number(code),
                          ),
                      )
                      .map(({ code, name }) => ({
                          id: code,
                          name: `${code} - ${name}`,
                      }))
                : [],
            isLoading: naicsCodes.isLoading,
        },
        categories: {
            options:
                categories.data?.map((category) => ({
                    id: category.name,
                    name: category.name,
                    children: category.children.map((child) => ({
                        id: child,
                        name: child,
                    })),
                })) ?? [],
            isLoading: categories.isLoading,
            selected: listBuilder ? listBuilder[FormParams.CATEGORIES] : [],
        },
    }),
);

interface IProps extends IWithClassName {
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: Array<IListItem>) => void;
    form: ICustomListsForm;
    onCheckBoxChange: (
        step: Steps,
        label: FormParams,
    ) => (event: ChangeEvent<HTMLInputElement>) => void;
    error: IError;
    initStepForm: (step: Steps, form: ICustomListsForm) => void;
}

const CustomLists = ({
    className,
    onSelectChange,
    form,
    onCheckBoxChange,
    error,
    initStepForm,
}: IProps) => {
    const [listNameError, setListNameError] = useState<string>("");
    const {
        naicsCodes,
        sicCodes,
        categories,
        listBuilderList,
        addTrackedLists,
        primaryNaicsCodesOnly,
        trackedLists,
    } = useSelector(selector);

    const selectedCategores = useMemo(
        () =>
            flatOptions(categories.options).filter(({ id }) =>
                categories.selected.includes(id),
            ),
        [categories],
    );

    const listFilterOptions = useMemo(() => {
        if (form.addTrackedLists) {
            return listBuilderList.options.map((item) => {
                const isInTrackedList = trackedLists.list.some(
                    (trackedItem) => trackedItem.id === item.id,
                );
                return {
                    id: item.id,
                    name: item.name,
                    readonly: isInTrackedList,
                    alwaysSelected: isInTrackedList,
                };
            });
        }
        return listBuilderList.options;
    }, [listBuilderList.options, form.addTrackedLists, trackedLists.list]);

    useEffect(() => {
        initStepForm(Steps.CUSTOM_LISTS, {
            [FormParams.SIC_CODES]: sicCodes.selected,
            [FormParams.NAICS_CODES]: naicsCodes.selected,
            [FormParams.CATEGORIES]: selectedCategores,
            [FormParams.LISTIDS]: listBuilderList.selected,
            [FormParams.ADD_TRACKED_LISTS]: addTrackedLists,
            [FormParams.PRIMARY_NAICS_CODE_ONLY]: primaryNaicsCodesOnly,
        });
    }, [
        sicCodes,
        naicsCodes,
        selectedCategores,
        listBuilderList,
        addTrackedLists,
        primaryNaicsCodesOnly,
    ]);

    const addTrackedListHint = useMemo(() => {
        if (!trackedLists.isLoading) {
            if (!trackedLists.list.length) {
                return "You are not currently tracking any lists";
            }
            const trackedListSet = new Set(
                trackedLists.list.map(({ id }) => id),
            );
            if (
                (form.listIds ?? []).some((list) => trackedListSet.has(list.id))
            ) {
                return "You've already tracking the entered list";
            }
        }
        return undefined;
    }, [trackedLists, form.listIds]);

    return (
        <Box className={className}>
            <Card className="code-form">
                <CardContent>
                    <Box className="code-form-row-1">
                        <FormControl className="select-input">
                            <FormLabel className="text-center">
                                SIC Codes:
                            </FormLabel>
                            <Box>
                                <MultiSelectList
                                    options={sicCodes.options}
                                    onChange={onSelectChange(
                                        Steps.CUSTOM_LISTS,
                                        FormParams.SIC_CODES,
                                    )}
                                    selectedItems={form.sicCodes ?? []}
                                    multiline={true}
                                    getChipText={({ id }) => id}
                                    menuWidth={400}
                                />
                                <FormHelperText className="Mui-error">
                                    {error[FormParams.SIC_CODES]}
                                </FormHelperText>
                            </Box>
                        </FormControl>
                        <FormControl className="select-input">
                            <FormLabel className="text-center">
                                NAICS codes:
                            </FormLabel>
                            <Box>
                                <MultiSelectList
                                    options={naicsCodes.options}
                                    onChange={onSelectChange(
                                        Steps.CUSTOM_LISTS,
                                        FormParams.NAICS_CODES,
                                    )}
                                    selectedItems={form.naicsCodes ?? []}
                                    multiline={true}
                                    getChipText={({ id }) => id}
                                    menuWidth={400}
                                />
                                <FormHelperText className="Mui-error">
                                    {error[FormParams.SIC_CODES]}
                                </FormHelperText>
                            </Box>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={onCheckBoxChange(
                                        Steps.CUSTOM_LISTS,
                                        FormParams.PRIMARY_NAICS_CODE_ONLY,
                                    )}
                                    checked={
                                        form[
                                            FormParams.PRIMARY_NAICS_CODE_ONLY
                                        ] ?? false
                                    }
                                />
                            }
                            label="Primary NAICS Codes Only"
                        />
                    </Box>

                    <Box className="code-form-row-1">
                        <FormControl className="select-input">
                            <FormLabel className="text-center">
                                List Name:
                            </FormLabel>
                            <Box>
                                <MultiSelectList
                                    disable={
                                        trackedLists.isLoading ||
                                        listBuilderList.isLoading
                                    }
                                    options={listFilterOptions}
                                    onChange={onSelectChange(
                                        Steps.CUSTOM_LISTS,
                                        FormParams.LISTIDS,
                                    )}
                                    selectedItems={form.listIds ?? []}
                                    multiline={true}
                                    menuWidth={300}
                                    getOptionTitle={(opt) =>
                                        opt.readonly
                                            ? "You've already tracking the entered list"
                                            : undefined
                                    }
                                    onEnterKeyError={(errorMessage) => {
                                        setListNameError(errorMessage);
                                        // Clear error after 3 seconds
                                        setTimeout(
                                            () => setListNameError(""),
                                            3000,
                                        );
                                    }}
                                />
                                <FormHelperText className="Mui-error">
                                    {listNameError || error[FormParams.LISTIDS]}
                                </FormHelperText>
                            </Box>
                        </FormControl>
                        <Tooltip title={addTrackedListHint}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={
                                            trackedLists.isLoading ||
                                            !!addTrackedListHint
                                        }
                                        onChange={onCheckBoxChange(
                                            Steps.CUSTOM_LISTS,
                                            FormParams.ADD_TRACKED_LISTS,
                                        )}
                                        checked={
                                            form[
                                                FormParams.ADD_TRACKED_LISTS
                                            ] ?? false
                                        }
                                    />
                                }
                                label="Add all of your Tracked Lists to this List Builder"
                            />
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>

            <DraggableSelector
                leftBoxHeader="Included Categories"
                rightBoxHeader="Excluded Categories"
                options={categories.options}
                selected={form.categories ?? []}
                onChange={onSelectChange(
                    Steps.CUSTOM_LISTS,
                    FormParams.CATEGORIES,
                )}
                addTitle="Add Category(s) to Include in List Builder"
                removeTitle="Remove field(s) from Include in List Builder"
                addAllTitle="Add All field(s) to Include in List Builder"
                removeAllTitle="Remove All field(s) from Include in List Builder"
                isTreeView={true}
                error={error[FormParams.CATEGORIES]}
            />
        </Box>
    );
};

export default styled(CustomLists)(({ theme }: IWithThemeProps) => ({
    display: "grid",
    gap: 20,
    ".code-form": {
        padding: theme?.spacing(2),
        ".code-form-row-1": {
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 10,
        },
        ".MuiFormControl-root": {
            flexDirection: "row",
        },
        ".code-form-row-2": {
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 10,
        },
        ".search-box": {
            backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
        },
        ".select-input .MuiBox-root": {
            width: "100%",
            margin: theme?.spacing("auto", 1),
        },
        ".MuiTextField-root": {
            width: "100%",
            ".MuiInput-root": {
                width: "inherit",
            },
        },
        ".MuiFormLabel-root": {
            whiteSpace: "nowrap",
        },
    },
}));
